/* ============================================================================
   <ba-slider> — Before / After drag comparison for Clinical Research section
   ========================================================================== */
if (!customElements.get('ba-slider')) {
  class BaSlider extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.cr-ba__frame');
      this.after = this.querySelector('.cr-ba__img--after');
      this.divider = this.querySelector('.cr-ba__divider');
      this.handle = this.querySelector('.cr-ba__handle');
      if (!this.frame || !this.handle) return;

      this.pos = parseFloat(this.dataset.start || '50');
      this.setPos(this.pos);

      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onKey = this.onKey.bind(this);

      this.handle.addEventListener('pointerdown', this.onDown);
      this.handle.addEventListener('keydown', this.onKey);
      this.frame.addEventListener('pointerdown', (e) => {
        if (e.target === this.handle) return;
        this.updateFromEvent(e);
        this.onDown(e);
      });
    }

    setPos(percent) {
      percent = Math.max(0, Math.min(100, percent));
      this.pos = percent;
      if (this.after) this.after.style.clipPath = `inset(0 0 0 ${percent}%)`;
      if (this.divider) this.divider.style.left = `${percent}%`;
      if (this.handle) {
        this.handle.style.left = `${percent}%`;
        this.handle.setAttribute('aria-valuenow', Math.round(percent));
      }
    }

    updateFromEvent(e) {
      const rect = this.frame.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = (x / rect.width) * 100;
      this.setPos(pct);
    }

    onDown(e) {
      e.preventDefault();
      this.handle.setPointerCapture?.(e.pointerId);
      document.addEventListener('pointermove', this.onMove);
      document.addEventListener('pointerup', this.onUp);
      document.addEventListener('pointercancel', this.onUp);
    }

    onMove(e) {
      this.updateFromEvent(e);
    }

    onUp() {
      document.removeEventListener('pointermove', this.onMove);
      document.removeEventListener('pointerup', this.onUp);
      document.removeEventListener('pointercancel', this.onUp);
    }

    onKey(e) {
      const step = e.shiftKey ? 10 : 2;
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.setPos(this.pos - step); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.setPos(this.pos + step); }
      else if (e.key === 'Home') { e.preventDefault(); this.setPos(0); }
      else if (e.key === 'End') { e.preventDefault(); this.setPos(100); }
    }
  }
  customElements.define('ba-slider', BaSlider);
}

/* Stat bars: staggered fill animation when the card first enters the viewport */
(function () {
  if (!('IntersectionObserver' in window)) return;

  const STAGGER_MS = 180;   // gap between each bar
  const START_DELAY_MS = 100; // delay before the first bar fills

  function primeStats(root) {
    // Cache target widths and reset to 0 so the CSS transition can play.
    root.querySelectorAll('.cr-stat__fill').forEach((fill) => {
      if (fill.dataset.primed === '1') return;
      const target = fill.style.width || getComputedStyle(fill).width;
      fill.dataset.targetWidth = target;
      fill.style.width = '0%';
      fill.dataset.primed = '1';
    });
  }

  function playStats(root) {
    const fills = root.querySelectorAll('.cr-stat__fill');
    fills.forEach((fill, i) => {
      const target = fill.dataset.targetWidth || fill.style.width;
      const delay = START_DELAY_MS + i * STAGGER_MS;
      setTimeout(() => {
        fill.style.width = target;
      }, delay);
    });
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      playStats(entry.target);
      io.unobserve(entry.target);
    });
  }, { threshold: 0.2 });

  function init() {
    // Prime + observe each card independently so day/night trigger separately.
    const cards = document.querySelectorAll('.cr-card');
    if (!cards.length) return;
    cards.forEach((card) => {
      primeStats(card);
      io.observe(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
